import { reportLoginFailure } from '../../api/audit';
import { useState, useCallback, useRef, useEffect } from 'react';
import {
    signInWithEmailAndPassword, signInWithPopup, PhoneAuthProvider,
    PhoneMultiFactorGenerator, getMultiFactorResolver, RecaptchaVerifier, getAuth,
} from 'firebase/auth';
import { firebaseAuthApp } from '../../firebase/auth';

export function loginErrorMessage(error, stage = 'login', hostname = window.location.hostname) {
    const code = error?.code;
    if (code === 'auth/invalid-app-credential' || code === 'auth/captcha-check-failed') {
        const local = ['localhost', '127.0.0.1', '[::1]'].includes(hostname);
        return `${stage === 'sms' ? 'MFA SMS verification' : 'Sign-in'} failed: Firebase rejected the app verification (${code}). ${local
            ? 'For SMS verification, use an authorized HTTPS development domain instead of localhost.'
            : 'Retry with a fresh verification. If this persists, check the authorized domain and reCAPTCHA configuration.'}`;
    }
    if (code === 'auth/unauthorized-domain') return 'This domain is not authorized for Firebase sign-in. Contact your administrator.';
    if (code === 'auth/network-request-failed') return 'Cannot reach Firebase. Check your connection and try again.';
    if (code === 'auth/too-many-requests') return 'Too many sign-in attempts. Please wait before trying again.';
    if (code === 'auth/invalid-verification-code') return 'Incorrect verification code. Please try again.';
    if (code === 'auth/code-expired') return 'Verification code expired. Please request a new code.';
    return error?.message || 'Sign-in failed. Please try again.';
}

export function useLoginWithMFA() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [mfaRequired, setMfaRequired] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState(null);
    const resolverRef = useRef(null);
    const verificationIdRef = useRef(null);
    const verifierRef = useRef(null);
    const busyRef = useRef(false);
    const attemptedAccount = useRef('');
    const reportFailure = (err, api) => {
        const allowed = ['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found', 'auth/user-disabled', 'auth/too-many-requests', 'auth/invalid-verification-code', 'auth/code-expired', 'auth/network-request-failed', 'auth/invalid-email'];
        if (attemptedAccount.current && allowed.includes(err?.code)) {
            void reportLoginFailure({ account: attemptedAccount.current, error_code: err.code, interface: api }).catch(() => { /* The login form must remain usable during an Admin outage. */ });
        }
    };

    const clearVerifier = useCallback(() => {
        const verifier = verifierRef.current;
        verifierRef.current = null;
        if (verifier) verifier.clear();
    }, []);
    useEffect(() => () => clearVerifier(), [clearVerifier]);

    const sendCode = useCallback(async (resolver) => {
        const hint = resolver.hints.find(h => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID);
        if (!hint) throw new Error('This account has no supported SMS second factor. Contact your administrator.');
        const auth = getAuth(firebaseAuthApp());
        clearVerifier();
        // A reCAPTCHA response is single-use. Each SMS request gets a fresh
        // verifier; verifyPhoneNumber performs the verification itself.
        const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
        verifierRef.current = verifier;
        try {
            const id = await new PhoneAuthProvider(auth).verifyPhoneNumber(
                { multiFactorHint: hint, session: resolver.session }, verifier,
            );
            verificationIdRef.current = id;
            setPhoneNumber(hint.phoneNumber);
            setMfaRequired(true);
        } finally {
            clearVerifier();
        }
    }, [clearVerifier]);

    const handleLogin = useCallback(async (loginFn) => {
        if (busyRef.current) return false;
        busyRef.current = true;
        setLoading(true);
        setError(null);
        setUser(null);
        setMfaRequired(false);
        setPhoneNumber(null);
        resolverRef.current = null;
        verificationIdRef.current = null;
        let stage = 'login';
        try {
            try {
                const credential = await loginFn();
                setUser(credential.user);
            } catch (err) {
                if (err.code !== 'auth/multi-factor-auth-required') throw err;
                stage = 'sms';
                const resolver = getMultiFactorResolver(getAuth(firebaseAuthApp()), err);
                resolverRef.current = resolver;
                await sendCode(resolver);
            }
            return true;
        } catch (err) {
            if (stage === 'login') reportFailure(err, 'auth.password');
            setError(loginErrorMessage(err, stage));
            return false;
        } finally {
            busyRef.current = false;
            setLoading(false);
        }
    }, [sendCode]);

    const loginWithEmail = useCallback((email, password) => {
        attemptedAccount.current = email.trim();
        return handleLogin(() => signInWithEmailAndPassword(getAuth(firebaseAuthApp()), email.trim(), password));
    }, [handleLogin]);
    const loginWithPopup = useCallback(provider => {
        attemptedAccount.current = '';
        return handleLogin(() => signInWithPopup(getAuth(firebaseAuthApp()), provider));
    }, [handleLogin]);

    const verifyCode = useCallback(async (code) => {
        if (busyRef.current) return false;
        if (!resolverRef.current || !verificationIdRef.current) {
            setError('Request a verification code before continuing.');
            return false;
        }
        busyRef.current = true;
        setLoading(true);
        setError(null);
        try {
            const credential = PhoneAuthProvider.credential(verificationIdRef.current, code);
            const result = await resolverRef.current.resolveSignIn(PhoneMultiFactorGenerator.assertion(credential));
            setUser(result.user);
            setMfaRequired(false);
            setPhoneNumber(null);
            resolverRef.current = null;
            verificationIdRef.current = null;
            return true;
        } catch (err) {
            reportFailure(err, 'auth.mfa');
            setError(loginErrorMessage(err, 'code'));
            return false;
        } finally {
            busyRef.current = false;
            setLoading(false);
        }
    }, []);

    const resendCode = useCallback(async () => {
        if (busyRef.current) return false;
        if (!resolverRef.current) {
            setError('Sign in again before requesting a verification code.');
            return false;
        }
        busyRef.current = true;
        setLoading(true);
        setError(null);
        try {
            await sendCode(resolverRef.current);
            return true;
        } catch (err) {
            setError(loginErrorMessage(err, 'sms'));
            return false;
        } finally {
            busyRef.current = false;
            setLoading(false);
        }
    }, [sendCode]);

    return { user, loading, error, mfaRequired, phoneNumber, loginWithEmail, loginWithPopup, verifyCode, resendCode };
}
